import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { ApiError, post } from './api';
import { vpathToNative } from '@zerogap/shared';
import { locale, msg, tr } from './i18n';
import { useSession } from './session';

// ───────── formatting ─────────
export function bytes(n: number | string | null | undefined): string {
  const v0 = Number(n ?? 0);
  if (!Number.isFinite(v0)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let v = Math.abs(v0);
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  // Isolated left-to-right so "38.2 GB" doesn't read "GB 38.2" inside Hebrew text.
  return `⁦${v >= 100 || u === 0 ? v.toFixed(0) : v.toFixed(1)} ${units[u]}⁩`;
}

export function ago(d: string | Date | null | undefined): string {
  if (!d) return tr('never');
  const s = Math.round((Date.now() - new Date(d).getTime()) / 1000);
  const abs = Math.abs(s);
  if (abs < 45) return tr('just now');
  const [n, unit]: [number, Intl.RelativeTimeFormatUnit] =
    abs < 3600 ? [Math.round(abs / 60), 'minute'] : abs < 86400 ? [Math.round(abs / 3600), 'hour'] : [Math.round(abs / 86400), 'day'];
  // Hebrew ICU writes the dual form with a redundant count: "לפני שעתיים (2)".
  return new Intl.RelativeTimeFormat(locale(), { numeric: 'auto' }).format(s >= 0 ? -n : n, unit).replace(/\s*\(\d+\)$/, '');
}

export function when(d: string | Date | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleString(locale(), { dateStyle: 'medium', timeStyle: 'short' });
}

/** An amount in the currency's minor unit (agorot for ILS), e.g. money(24900, 'ils') → "‏249 ₪". */
export function money(minor: number | string | null | undefined, currency: string | null | undefined = 'ils'): string {
  const v = Number(minor ?? 0) / 100;
  if (!Number.isFinite(v)) return '—';
  const whole = Number.isInteger(v);
  return v.toLocaleString(locale(), { style: 'currency', currency: (currency || 'ils').toUpperCase(), minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
}

export function vdisplay(p: string, style: 'windows' | 'posix' | string = 'posix'): string {
  if (style === 'windows' && p === '/') return tr('This PC');
  return vpathToNative(p, style);
}

const REASONS: Record<string, string> = {
  no_recovery_point: msg('Waiting for its first backup'),
  stale: msg('Backup overdue'),
  no_policy: msg('No backup plan'),
  paused: msg('Backups paused'),
  pending: msg('Setting up…'),
  quarantined: msg('Blocked'),
  retired: msg('Removed'),
};

/** How long ago, as a plain duration without "ago": "5 minutes", "an hour", "2 days". */
export function duration(ms: number): string {
  const min = Math.max(1, Math.round(ms / 60_000));
  if (min < 60) return min === 1 ? tr('a minute') : tr('{n} minutes', { n: min });
  const h = Math.round(min / 60);
  if (h < 48) return h === 1 ? tr('an hour') : h === 2 ? tr('2 hours') : tr('{n} hours', { n: h });
  const d = Math.round(h / 24);
  return d === 2 ? tr('2 days') : tr('{n} days', { n: d });
}

const ERRORS: Record<string, string> = {
  policy_incomplete: msg('This backup plan is not finished yet: choose at least one folder to back up.'),
  policy_in_use: msg('Some computers still use this plan. Move them to another plan first.'),
  email_unverified: msg('Please confirm your email address first. We sent you a link.'),
  no_policy: msg('This computer has no backup plan yet. Choose one first.'),
  tenant_restricted: msg("Your organization can't make changes right now. Check your subscription."),
  seat_limit: msg('Your subscription has no free user places left.'),
  device_limit: msg('Your subscription has no free computer places left.'),
  overwrite_unconfirmed: msg('Please confirm that existing files may be replaced.'),
  last_admin: msg('An organization always needs at least one admin.'),
  job_active: msg('A backup is already running on this computer.'),
  device_not_active: msg('This computer cannot back up right now: it is still being set up, blocked, or removed.'),
  not_ready: msg('This is not ready yet. Please try again in a moment.'),
  validation: msg('Some details are not valid. Please check them and try again.'),
  forbidden: msg("You don't have permission to do this."),
  internal: msg('Something went wrong on our side. Please try again.'),
  rate_limited: msg('Too many attempts. Please wait a moment and try again.'),
  csrf: msg('Your session has expired. Refresh the page and try again.'),
  email_in_use: msg('An account with this email already exists. Sign in instead.'),
};

/** A plain-language message for an API error; falls back to the server's own message. */
export function friendlyError(e: unknown): string {
  const x = e as ApiError | undefined;
  const text = x?.code ? ERRORS[x.code] : undefined;
  if (text) return tr(text);
  return x?.message ? tr(x.message) : String(e);
}
/** Why a computer is not protected. */
export function reasonLabel(reason: string): string {
  const r = REASONS[reason];
  return r ? tr(r) : reason;
}

// ───────── primitives ─────────
export function PageHead({ eyebrow, title, desc, actions }: { eyebrow?: ReactNode; title: ReactNode; desc?: ReactNode; actions?: ReactNode }) {
  return (
    <>
      <div className="page-head">
        <div className="titles">
          {eyebrow && <div className="eyebrow">{eyebrow}</div>}
          <h1>{title}</h1>
          {desc && <p>{desc}</p>}
        </div>
        {actions && <div className="actions">{actions}</div>}
      </div>
      <div className="rule" />
    </>
  );
}

/** With `onToggle`, the header becomes a button that opens and closes the card; `collapsed` hides the body. */
export function Card({ title, actions, children, pad = true, className = '', collapsed, onToggle }: {
  title?: ReactNode; actions?: ReactNode; children: ReactNode; pad?: boolean; className?: string; collapsed?: boolean; onToggle?: () => void;
}) {
  return (
    <section className={`card ${onToggle ? 'collapsible' : ''} ${collapsed ? 'collapsed' : ''} ${className}`}>
      {title && (
        <div className="card-head">
          {onToggle ? (
            <h2><button type="button" className="card-toggle" aria-expanded={!collapsed} onClick={onToggle}><span className="card-chev" aria-hidden />{title}</button></h2>
          ) : (
            <h2>{title}</h2>
          )}
          {actions && <div className="row">{actions}</div>}
        </div>
      )}
      {!collapsed && <div className={pad ? 'card-pad' : ''}>{children}</div>}
    </section>
  );
}

type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'neutral' | 'brass';
export function Badge({ tone = 'neutral', children, dot = true }: { tone?: Tone; children: ReactNode; dot?: boolean }) {
  return <span className={`badge b-${tone} ${dot ? '' : 'nodot'}`}>{children}</span>;
}

const STATE_TONES: Record<string, Tone> = {
  completed: 'ok', committed: 'ok', active: 'ok', ready: 'ok', success: 'ok', restore_verified: 'ok', sample_verified: 'ok', protected: 'ok', online: 'ok',
  partial: 'warn', retrying: 'warn', committing: 'info', running: 'info', preparing: 'info', queued: 'neutral', pending: 'neutral', upload_verified: 'info',
  failed: 'bad', expired: 'neutral', canceled: 'neutral', quarantined: 'bad', retired: 'neutral', suspended: 'warn', dead: 'bad', unprotected: 'bad', offline: 'warn',
  pending_deletion: 'bad', trialing: 'info', past_due: 'warn', unpaid: 'bad',
};
const STATE_LABEL: Record<string, string> = {
  upload_verified: 'Upload verified', sample_verified: 'Sample verified', restore_verified: 'Restore verified', pending_deletion: 'Pending deletion', past_due: 'Past due',
};
/** Human label for a lifecycle state such as `past_due`, in the current language. */
export function stateLabel(state: string): string {
  return tr(STATE_LABEL[state] ?? state.charAt(0).toUpperCase() + state.slice(1).replaceAll('_', ' '));
}
export function StateBadge({ state }: { state: string | null | undefined }) {
  if (!state) return <Badge>—</Badge>;
  return <Badge tone={STATE_TONES[state] ?? 'neutral'}>{stateLabel(state)}</Badge>;
}

export function Button({ variant = '', size = '', busy, children, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: string; size?: string; busy?: boolean }) {
  return (
    <button {...rest} className={`btn ${variant} ${size} ${rest.className ?? ''}`} disabled={rest.disabled || busy}>
      {busy && <span className="spin" aria-hidden />}
      {children}
    </button>
  );
}

export function Field({ label, hint, error, children }: { label: ReactNode; hint?: ReactNode; error?: string | null; children: ReactNode }) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint && <div className="hint">{hint}</div>}
      {error && <div className="err" role="alert">{error}</div>}
    </div>
  );
}

/** ZeroGap mark (small-size cut). The two fills follow the theme: ink and brass on light, the reversed
 *  palette on dark (--logo-o / --logo-c in styles.css). Master: docs/brand/logo. */
export function BrandMark({ size, className }: { size?: number; className?: string }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 256 256" aria-hidden>
      <path className="lg-c" fillRule="evenodd" d="M40 100A88 88 0 0 1 216 100V156A88 88 0 0 1 40 156ZM96 100A32 32 0 0 1 160 100V156A32 32 0 0 1 96 156Z" />
      <path className="lg-o" d="M183.56 31.76A88 88 0 0 1 216 100V156A88 88 0 0 1 72.44 224.24L101.49 173.92A32 32 0 0 0 160 156V100A32 32 0 0 0 154.51 82.08Z" />
    </svg>
  );
}

// ───────── colour theme ─────────
// Light is the default for everyone, whatever the computer's own setting; dark is a choice made here.
// The choice is shared with the landing page (same key) and applied before first paint by public/theme-boot.js.
type Theme = 'light' | 'dark';
const currentTheme = (): Theme => (document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light');

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  // Switch every colour in one frame: transitions are suppressed for the swap and restored after it.
  root.classList.add('theme-swap');
  root.dataset.theme = theme;
  void root.offsetWidth;
  requestAnimationFrame(() => root.classList.remove('theme-swap'));
  try {
    localStorage.setItem('zg-theme', theme);
  } catch {
    // storage blocked: the choice lasts until the page is reloaded
  }
}

/** Switches between the light and dark look. Labelled with the look it switches to. */
export function ThemeSwitch({ className }: { className?: string }) {
  const [theme, setTheme] = useState<Theme>(currentTheme);
  const next: Theme = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      className={`theme-switch ${className ?? ''}`}
      onClick={() => { applyTheme(next); setTheme(next); }}
    >
      {next === 'dark' ? (
        <svg viewBox="0 0 20 20" aria-hidden><path d="M16.5 12.4A7 7 0 0 1 7.6 3.5a7 7 0 1 0 8.9 8.9z" /></svg>
      ) : (
        <svg viewBox="0 0 20 20" aria-hidden><circle cx="10" cy="10" r="3.6" /><path d="M10 1.8v2M10 16.2v2M1.8 10h2M16.2 10h2M4.2 4.2l1.4 1.4M14.4 14.4l1.4 1.4M4.2 15.8l1.4-1.4M14.4 5.6l1.4-1.4" /></svg>
      )}
      {next === 'dark' ? tr('Dark mode') : tr('Light mode')}
    </button>
  );
}

export function Empty({ glyph = '§', title, children }: { glyph?: string; title: ReactNode; children?: ReactNode }) {
  return (
    <div className="empty">
      <div className="glyph" aria-hidden>{glyph}</div>
      <h3>{title}</h3>
      {children && <div className="muted" style={{ marginTop: 6 }}>{children}</div>}
    </div>
  );
}

export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div className="stack" style={{ gap: 10 }} aria-busy="true" aria-label={tr('Loading')}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}

export function ErrorBox({ error, retry }: { error: unknown; retry?: () => void }) {
  const e = error as ApiError;
  return (
    <div className="notice bad" role="alert">
      <strong>{e?.status === 403 ? tr('Not allowed') : tr('Something went wrong')}</strong>
      <div>{friendlyError(error)}</div>
      {e?.requestId && <div className="small muted">{tr('Reference:')} <span className="mono">{e.requestId}</span></div>}
      {retry && <Button size="sm" onClick={retry} style={{ marginTop: 8 }}>{tr('Try again')}</Button>}
    </div>
  );
}

export function Progress({ value }: { value: number }) {
  return (
    <div className="progress" role="progressbar" aria-valuenow={Math.round(value)} aria-valuemin={0} aria-valuemax={100}>
      <span style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const focusables = (root: HTMLElement) => [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);

/** A table row's real control: the row opens on click as a convenience, this link is what keyboards and screen readers use. */
export function RowLink({ to, children }: { to: string; children: ReactNode }) {
  return <Link to={to} className="row-link" onClick={(e) => e.stopPropagation()}>{children}</Link>;
}

export function Modal({ title, children, footer, onClose, wide }: { title: ReactNode; children: ReactNode; footer?: ReactNode; onClose: () => void; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  // IS 5568 / WCAG 2.4.3: move focus into the dialog on open and give it back on close. Focus goes to the
  // first field, never to a footer button (that could be "Delete"); with no field, to the dialog itself.
  useEffect(() => {
    const dialog = ref.current!;
    const opener = document.activeElement as HTMLElement | null;
    if (!dialog.contains(document.activeElement)) {
      const field = focusables(dialog).find((el) => el.closest('.modal-body') && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName));
      (field ?? dialog).focus();
    }
    return () => { if (opener?.isConnected) opener.focus(); };
  }, []);
  // Keep Tab inside the dialog. Stopping propagation keeps a dialog opened from inside another one from
  // handing the key on to its parent, whose own trap would pull focus back out.
  const trap = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    e.stopPropagation();
    const list = focusables(ref.current!);
    if (!list.length) { e.preventDefault(); return; }
    const first = list[0]!, last = list[list.length - 1]!;
    if (e.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  // Portaled to <body>: an animated, filtered or transformed ancestor would otherwise become the
  // containing block for the fixed backdrop, shrinking it to that ancestor and pushing the dialog off-screen.
  return createPortal(
    <div className="backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} tabIndex={-1} onKeyDown={trap} className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="modal-head">
          <h2 id={titleId}>{title}</h2>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/**
 * WAI-ARIA tabs: one Tab stop (the selected tab), arrow keys move between tabs in reading direction
 * (mirrored in Hebrew), Home/End jump to the ends. tabIndex -1 on the strip keeps its sideways scroll
 * area from becoming a stray Tab stop of its own.
 */
export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { key: T; label: ReactNode }[]; value: T; onChange: (v: T) => void }) {
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex((t) => t.key === value);
    const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
    const step = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 }[e.key];
    const next = step !== undefined ? (i + step + tabs.length) % tabs.length : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : -1;
    if (next < 0) return;
    e.preventDefault();
    onChange(tabs[next]!.key);
    e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
  };
  return (
    <div className="tabs" role="tablist" tabIndex={-1} onKeyDown={onKeyDown}>
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={value === t.key} tabIndex={value === t.key ? 0 : -1} className={value === t.key ? 'on' : ''} onClick={() => onChange(t.key)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({ options, value, onChange }: { options: { key: T; label: ReactNode }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="seg" role="radiogroup">
      {options.map((o) => (
        <button key={o.key} type="button" role="radio" aria-checked={value === o.key} className={value === o.key ? 'on' : ''} onClick={() => onChange(o.key)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function CopyBox({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="stack" style={{ gap: 6 }}>
      {label && <div className="small muted">{label}</div>}
      <div className="row" style={{ alignItems: 'stretch', flexWrap: 'nowrap' }}>
        <div className="codebox" style={{ flex: 1 }}>{text}</div>
        <Button
          size="sm"
          onClick={async () => {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? tr('Copied') : tr('Copy')}
        </Button>
      </div>
    </div>
  );
}

export function Pager({ page, pageSize, total, onChange }: { page: number; pageSize: number; total: number; onChange: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="row" style={{ justifyContent: 'flex-end', padding: '12px 16px' }}>
      <span className="small muted">
        <span className="ltr">{page * pageSize + 1}–{Math.min(total, (page + 1) * pageSize)}</span> {tr('of {total}', { total })}
      </span>
      <Button size="sm" disabled={page === 0} onClick={() => onChange(page - 1)}>{tr('Previous')}</Button>
      <Button size="sm" disabled={page >= pages - 1} onClick={() => onChange(page + 1)}>{tr('Next')}</Button>
    </div>
  );
}

// ───────── toasts ─────────
type Toast = { id: number; text: string; bad?: boolean };
const ToastCtx = createContext<(text: string, bad?: boolean) => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const n = useRef(0);
  const push = useCallback((text: string, bad?: boolean) => {
    const id = ++n.current;
    setToasts((t) => [...t, { id, text, bad }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.bad ? 'bad' : ''}`}>{t.text}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

// ───────── step-up ─────────
export function StepUpDialog({ mfa, onDone, onClose }: { mfa: boolean; onDone: () => void; onClose: () => void }) {
  const { me } = useSession();
  if (me && !me.user.hasPassword) return <GoogleStepUpDialog onClose={onClose} />;
  return <PasswordStepUpDialog mfa={mfa} onDone={onDone} onClose={onClose} />;
}

/**
 * An account made with Google has no password to re-enter: it confirms by signing in to Google again. That
 * leaves the page, so the person repeats the action when they are back (the confirmation lasts a few minutes).
 */
function GoogleStepUpDialog({ onClose }: { onClose: () => void }) {
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      title={tr("Confirm it's you")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{tr('Cancel')}</Button>
          <Button
            variant="primary"
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                const r = await post<{ url: string }>('/api/auth/google/step-up', { next: window.location.pathname + window.location.search });
                window.location.assign(r.url);
              } catch (e) {
                setErr((e as Error).message);
                setBusy(false);
              }
            }}
          >
            {tr('Continue with Google')}
          </Button>
        </>
      }
    >
      <p className="muted" style={{ marginTop: 0 }}>{tr('This action is sensitive. Sign in with Google again to confirm, then try the action once more.')}</p>
      {err && <div className="notice bad">{err}</div>}
    </Modal>
  );
}

function PasswordStepUpDialog({ mfa, onDone, onClose }: { mfa: boolean; onDone: () => void; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      title={tr("Confirm it's you")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{tr('Cancel')}</Button>
          <Button
            variant="primary"
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await post('/api/auth/step-up', { password, code: mfa ? code : undefined });
                onDone();
              } catch (e) {
                setErr((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {tr('Confirm')}
          </Button>
        </>
      }
    >
      <p className="muted" style={{ marginTop: 0 }}>
        {mfa ? tr('This action is sensitive. Re-enter your password and a code from your authenticator app.') : tr('This action is sensitive. Re-enter your password.')}
      </p>
      <Field label={tr('Password')}>
        <input type="password" autoFocus autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
      {mfa && (
        <Field label={tr('Authentication code')}>
          <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
      )}
      {err && <div className="notice bad">{err}</div>}
    </Modal>
  );
}

/** Wrap an action that may require step-up: retries it after a successful re-authentication. */
export function useStepUp(mfa: boolean) {
  const [pending, setPending] = useState<null | (() => Promise<void>)>(null);
  const run = useCallback(async (fn: () => Promise<void>) => {
    try {
      await fn();
    } catch (e) {
      if ((e as ApiError).code === 'step_up_required') setPending(() => fn);
      else throw e;
    }
  }, []);
  const dialog = pending ? (
    <StepUpDialog
      mfa={mfa}
      onClose={() => setPending(null)}
      onDone={async () => {
        const fn = pending;
        setPending(null);
        await fn();
      }}
    />
  ) : null;
  return { run, dialog };
}

// ───────── plain-language building blocks ─────────

/** A small "?" that explains a term in plain words. */
export function HelpTip({ children, label }: { children: ReactNode; label?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="helptip">
      <button type="button" className="helptip-btn" aria-label={label ?? tr('What does this mean?')} aria-expanded={open} onClick={() => setOpen(!open)} onBlur={() => setOpen(false)}>?</button>
      {open && <span role="tooltip" className="helptip-pop">{children}</span>}
    </span>
  );
}

/** Collapsed area for details that support staff or IT people need, never the main path. */
export function TechDetails({ summary, children, open, inline }: { summary?: ReactNode; children: ReactNode; open?: boolean; inline?: boolean }) {
  return (
    <details className={`tech ${inline ? 'inline' : ''}`} open={open}>
      <summary>{summary ?? tr('Technical details')}</summary>
      <div className="tech-body">{children}</div>
    </details>
  );
}

/** Asks before something surprising or permanent, saying plainly what will happen. */
export function ConfirmDialog({ title, children, confirm, danger, onConfirm, onClose }: {
  title: ReactNode;
  children: ReactNode;
  confirm: ReactNode;
  danger?: boolean;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{tr('Cancel')}</Button>
          <Button variant={danger ? 'danger solid' : 'primary'} busy={busy} onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              await onConfirm();
            } catch (e) {
              setErr(friendlyError(e));
            } finally {
              setBusy(false);
            }
          }}>{confirm}</Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 10 }}>{children}</div>
      {err && <div className="notice bad" style={{ marginTop: 12 }}>{err}</div>}
    </Modal>
  );
}

/** Large radio choices with a title and a one-line explanation (e.g. "Several times a day"). */
export function OptionCards<T extends string>({ name, options, value, onChange, disabled }: {
  name: string;
  options: { key: T; title: ReactNode; desc?: ReactNode; extra?: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="optcards" role="radiogroup">
      {options.map((o) => (
        <div key={o.key} className={`optcard ${value === o.key ? 'on' : ''} ${disabled ? 'disabled' : ''}`}>
          <label>
            <input type="radio" name={name} checked={value === o.key} disabled={disabled} onChange={() => onChange(o.key)} />
            <span className="optcard-text">
              <span className="optcard-title">{o.title}</span>
              {o.desc && <span className="optcard-desc">{o.desc}</span>}
            </span>
          </label>
          {value === o.key && o.extra && <div className="optcard-extra">{o.extra}</div>}
        </div>
      ))}
    </div>
  );
}

// ───────── icons (inline, currentColor) ─────────
const I = (d: string) =>
  function Icon(props: React.SVGProps<SVGSVGElement>) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden {...props}>
        <path d={d} />
      </svg>
    );
  };
export const Icons = {
  dash: I('M3 13h8V3H3zM13 21h8v-8h-8zM13 3v6h8V3zM3 21h8v-4H3z'),
  devices: I('M3 5h18v11H3zM8 20h8M12 16v4'),
  policy: I('M12 3l8 3v6c0 4.5-3.4 8.4-8 9-4.6-.6-8-4.5-8-9V6z'),
  restore: I('M3 12a9 9 0 1 0 3-6.7M3 4v5h5'),
  users: I('M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 10a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M22 19v-1a4 4 0 0 0-3-3.9M16 3.1a3.5 3.5 0 0 1 0 6.8'),
  audit: I('M4 4h16v16H4zM8 9h8M8 13h8M8 17h5'),
  bell: I('M18 8a6 6 0 1 0-12 0c0 7-3 8-3 8h18s-3-1-3-8M13.7 21a2 2 0 0 1-3.4 0'),
  hook: I('M18 16.1A4 4 0 1 1 13 20h-2M6 16l4-7a4 4 0 1 1 7.7-1.5M12 11l3 5'),
  bill: I('M3 6h18v12H3zM3 10h18M7 15h3'),
  gear: I('M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1'),
  shield: I('M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10M9 12l2 2 4-4'),
  alert: I('M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0M12 9v4M12 17h.01'),
  folder: I('M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'),
  file: I('M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6'),
  link: I('M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7'),
  server: I('M3 4h18v6H3zM3 14h18v6H3zM7 7h.01M7 17h.01'),
  queue: I('M4 6h16M4 12h16M4 18h10'),
  rocket: I('M5 15c-1.5 1.3-2 5-2 5s3.7-.5 5-2c.7-.8.7-2 0-2.8a2 2 0 0 0-3-.2M12 15l-3-3a22 22 0 0 1 2-4A12.9 12.9 0 0 1 22 2c0 2.7-.8 7.5-6 11a22 22 0 0 1-4 2M9 12H4s.6-3 2-4c1.6-1.1 5 0 5 0M12 15v5s3-.6 4-2c1.1-1.6 0-5 0-5'),
  wrench: I('M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.8-3.8a6 6 0 0 1-7.9 7.9l-6.9 6.9a2.1 2.1 0 0 1-3-3l6.9-6.9a6 6 0 0 1 7.9-7.9z'),
  user: I('M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8'),
  download: I('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3'),
  plus: I('M12 5v14M5 12h14'),
  clock: I('M12 7v5l3 2M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18'),
  history: I('M4 4v5h5M4.6 15a8 8 0 1 0 1.9-8.3L4 9M12 8v4l3 2'),
  x: I('M6 6l12 12M18 6 6 18'),
  share: I('M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v13'),
  chat: I('M21 11.5a8.4 8.4 0 0 1-9 8.4 9.9 9.9 0 0 1-4.1-.9L3 20.5l1.5-4.4A8.4 8.4 0 0 1 3.6 11.5 8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4z'),
};
